import React, { useState, useEffect } from "react";
import { Button, Dropdown, DropdownItemProps, Modal } from "semantic-ui-react";
import { ApiKey, Role } from "../../../../util";
import { StyledInput, StyledLabel } from "../actiontypes/ActionTypesModal";
import { ErrorMessage } from "../roles/CreateOrEditRoleModal";

interface CreateOrEditApiKeyModalProps {
  apiKey: { name: string; role: string };
  allApiKeys: ApiKey[];
  title: string;
  onSubmit: (ApiKey) => void;
  trigger: React.ReactNode;
  roles: Role[];
}

export default function CreateOrEditApiKeyModal(
  props: CreateOrEditApiKeyModalProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = useState<DropdownItemProps[]>([]);
  const [apikey, setApikey] = useState<{ name: string; role: string }>(
    props.apiKey
  );
  const [allApiNames, setAllApiNames] = useState<Set<string>>(new Set());
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();

  const onModalClose = () => {
    setOpen(false);
    if (props.apiKey.name === "" && props.apiKey.role === "") {
      setApikey({
        name: "",
        role: "",
      });
    }
    setErrorMessage("");
  };

  useEffect(() => {
    setOptions(
      props.roles.map((role, index) => ({
        key: role.id,
        text: role.name,
        value: index,
      }))
    );

    // Set all the api-names in the set.
    if (props.allApiKeys.length > 0) {
      const namesSet = new Set(
        props.allApiKeys.map((user) => user.name?.toLowerCase())
      );
      if (apikey.name) {
        namesSet.delete(apikey.name.toLowerCase()); // Remove the current name from the set
      }

      setAllApiNames(namesSet);
    }
  }, [props.roles]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (apikey.role.length > 0 && apikey.name.length > 0) {
      if (apikey.name === props.apiKey.name) {
        setButtonDisabled(true);
        return;
      } else {
        setButtonDisabled(false);
      }
    } else {
      setButtonDisabled(true);
    }
  }, [apikey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      className="dark"
      onClose={() => onModalClose()}
      onOpen={() => setOpen(true)}
      open={open}
      size="mini"
      trigger={props.trigger}
    >
      <Modal.Header>{props.title}</Modal.Header>
      <Modal.Description></Modal.Description>
      <Modal.Content>
        <StyledInput labelPosition="left">
          <StyledLabel>Name</StyledLabel>
          <input
            autoFocus
            placeholder="API Key Name"
            value={apikey.name}
            onChange={(e) => {
              setApikey({
                ...apikey,
                name: e.target.value,
              });
            }}
          />
        </StyledInput>
        {errorMessage && (
          <ErrorMessage id={"userEmailErrorSpan"}>{errorMessage}</ErrorMessage>
        )}
        <StyledInput labelPosition="left">
          <StyledLabel>Select Role</StyledLabel>
          <Dropdown
            placeholder="Please your select Role"
            fluid
            selection
            search
            options={options}
            disabled={props.title.toLowerCase().includes("edit")}
            defaultValue={
              props.title.toLowerCase().includes("edit")
                ? props.roles.findIndex((role) => role.name === apikey.role)
                : undefined
            }
            onChange={(_e, d) => {
              setApikey({
                ...apikey,
                role: props.roles[d.value as number]["name"],
              });
            }}
            style={{
              padding: "8px 12px",
              border: "none",
            }}
          />
        </StyledInput>
      </Modal.Content>
      <Modal.Actions>
        <Button
          secondary
          onClick={() => {
            onModalClose();
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          primary
          disabled={buttonDisabled}
          onClick={() => {
            // Check if the key name already exists.
            if (allApiNames.has(apikey.name.toLowerCase())) {
              setErrorMessage("Key name already exists.");
            } else {
              onModalClose();
              props.onSubmit(apikey);
            }
          }}
        >
          Submit
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
